'use client';

import {Button, Image} from '@udemy-v2/react-core-components';
import {useI18n} from '@udemy-v2/i18n';
import {useMatchMedia} from '@udemy-v2/design-system-utils';
import classNames from 'classnames';

import {InstructorCommunityBannerInfoByInstructorIdQuery} from '@/generated/graphql';
import styles from './instructor-community-banner.module.scss';

export type InstructorCommunityBannerInfo = NonNullable<
    InstructorCommunityBannerInfoByInstructorIdQuery['instructorCommunityBannerInfoByInstructorId']
>;

const BANNER_TEXT_COLOR = {
    DARK: 'var(--applied-text-default)',
    LIGHT: '#E9EAF2',
} as const;

export interface InstructorCommunityBannerProps {
    instructorName: string;
    instructorUrl?: string;
    bannerInfo: InstructorCommunityBannerInfo;
}

export function InstructorCommunityBanner({
    instructorName,
    instructorUrl,
    bannerInfo,
}: InstructorCommunityBannerProps) {
    const {gettext, interpolate} = useI18n();
    const isMobile = useMatchMedia('sm-max');

    const bannerImageUrl = isMobile
        ? bannerInfo.mobileCommunityBannerImage
        : bannerInfo.communityBannerImage;

    if (!bannerImageUrl) {
        return null;
    }

    const textColor = isMobile
        ? BANNER_TEXT_COLOR.LIGHT
        : BANNER_TEXT_COLOR[bannerInfo.communityBannerTextColorPreference ?? 'DARK'];
    const isLightText = textColor === BANNER_TEXT_COLOR.LIGHT;

    const bannerTitle = interpolate(
        gettext("Join %(instructorName)s's Learning Community"),
        {instructorName},
        true,
    );

    const primaryButtonText = bannerInfo.isSubscribed
        ? gettext('Go to community')
        : gettext('Join my community');
    const primaryButtonHref = bannerInfo.isSubscribed
        ? bannerInfo.communityUrl
        : bannerInfo.communityPaywallUrl;

    const bannerContent = (
        <>
            <h3 className={classNames('ud-heading-lg', styles['banner-title'])}>{bannerTitle}</h3>
            {bannerInfo.description && (
                <p className={classNames('ud-text-sm', styles['banner-description'])}>
                    {bannerInfo.description}
                </p>
            )}
            <div className={styles['banner-buttons']}>
                <Button
                    udStyle="primary"
                    size="small"
                    componentClass="a"
                    href={primaryButtonHref}
                >
                    {primaryButtonText}
                </Button>
                <Button
                    udStyle={isLightText ? 'knockout-outline' : 'secondary'}
                    size="small"
                    componentClass="a"
                    href={instructorUrl}
                >
                    {gettext('Learn more')}
                </Button>
            </div>
        </>
    );

    if (isMobile) {
        return (
            <div
                className={styles['banner-container-mobile']}
                data-purpose="instructor-community-banner-mobile"
            >
                <div className={styles['banner-photo-mobile']}>
                    <Image src={bannerImageUrl} alt="" />
                </div>
                <div className={styles['banner-content-mobile']} style={{color: textColor}}>
                    {bannerContent}
                </div>
            </div>
        );
    }

    return (
        <div
            className={styles['banner-container']}
            style={{backgroundImage: `url(${bannerImageUrl})`}}
            data-purpose="instructor-community-banner"
        >
            <div className={styles['banner-content']} style={{color: textColor}}>
                {bannerContent}
            </div>
        </div>
    );
}
